import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Users,
  Plus,
  Search,
  Download,
  Upload,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  MapPin,
  TrendingUp,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  Factory,
} from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useClients, useDeleteClient } from '@/hooks/queries/useClients';
import { SupplierForm } from '@/components/suppliers/SupplierForm';
import { toast } from '@/components/ui/toast/useToast';
import type { Client } from '@/types/client';

export const Route = createFileRoute('/suppliers/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: SuppliersPage,
});

function SuppliersPage() {
  const { company } = useAuthStore();
  
  // ✅ HARDCODED to 'supplier' type only
  const { data: suppliers = [], isLoading, error, refetch } = useClients(
    company?.id || '',
    'supplier'
  );
  
  const deleteClient = useDeleteClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Client | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const filteredSuppliers = suppliers.filter((supplier) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      supplier.name.toLowerCase().includes(searchLower) ||
      supplier.phone?.includes(searchQuery) ||
      (supplier.email && supplier.email.toLowerCase().includes(searchLower)) ||
      supplier.wilaya?.toLowerCase().includes(searchLower)
    );
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const stats = {
    total: suppliers.length,
    totalBalance: suppliers.reduce((sum, c) => sum + (c.balance || 0), 0),
    totalPurchases: suppliers.reduce((sum, c) => sum + (c.total_purchases || 0), 0),
    avgPurchase:
      suppliers.length > 0
        ? suppliers.reduce((sum, c) => sum + (c.total_purchases || 0), 0) / suppliers.length
        : 0,
  };

  const handleEdit = (supplier: Client) => {
    setSelectedSupplier(supplier);
    setFormOpen(true);
  };

  const handleView = (supplier: Client) => {
    setSelectedSupplier(supplier);
    setViewOpen(true);
  };

  const handleNew = () => {
    setSelectedSupplier(null);
    setFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteClient.mutateAsync(id);
        toast.success(
          'تم الحذف بنجاح / Supprimé avec succès',
          'تم حذف المورد / Le fournisseur a été supprimé'
        );
        setDeleteConfirmId(null);
      } catch (error) {
        toast.error('خطأ / Erreur', 'فشل الحذف / Échec de la suppression');
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              الموردون / Fournisseurs
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              إدارة قاعدة بيانات الموردين / Gérez votre base de fournisseurs
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              تحديث / Actualiser
            </Button>
            <Button variant="outline" size="sm">
              <Upload className="w-4 h-4 mr-2" />
              استيراد / Importer
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              تصدير / Exporter
            </Button>
            <Button onClick={handleNew} className="gap-2 bg-orange-600 hover:bg-orange-700">
              <Plus className="w-5 h-5" />
              مورد جديد / Nouveau
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
                الموردون / Fournisseurs
              </CardTitle>
              <Factory className="w-4 h-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.total}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
                الرصيد / Solde total
              </CardTitle>
              <TrendingUp className="w-4 h-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(stats.totalBalance)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
                إجمالي المشتريات / Total achats
              </CardTitle>
              <TrendingUp className="w-4 h-4 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {formatCurrency(stats.totalPurchases)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
                متوسط / Moyenne
              </CardTitle>
              <TrendingUp className="w-4 h-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {formatCurrency(stats.avgPurchase)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="بحث بالاسم، الهاتف، البريد أو الولاية / Rechercher par nom, tél, email ou wilaya..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Suppliers List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">
              قائمة الموردين / Liste des fournisseurs ({filteredSuppliers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-red-600">
                <AlertCircle className="w-12 h-12 mb-4" />
                <p>خطأ في التحميل / Erreur de chargement</p>
              </div>
            ) : filteredSuppliers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Factory className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">
                  لا يوجد موردون / Aucun fournisseur
                </p>
                <p className="text-sm mb-4">
                  أضف مورد جديد للبدء / Ajoutez un fournisseur pour commencer
                </p>
                <Button onClick={handleNew} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="w-4 h-4 mr-2" />
                  مورد جديد / Nouveau fournisseur
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        المورد / FOURNISSEUR
                      </th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        الاتصال / CONTACT
                      </th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        الولاية / WILAYA
                      </th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        NIF / RC
                      </th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        الرصيد / SOLDE
                      </th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        إجراءات / ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredSuppliers.map((supplier) => (
                      <tr
                        key={supplier.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center">
                              <span className="text-orange-600 dark:text-orange-400 font-bold text-lg">
                                {supplier.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {supplier.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {supplier.address || '-'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Phone className="w-3 h-3" />
                              {supplier.phone}
                            </div>
                            {supplier.email && (
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <Mail className="w-3 h-3" />
                                {supplier.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {supplier.wilaya || '-'}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                          <div>{supplier.tax_id || '-'}</div>
                        </td>
                        <td className="p-3">
                          <span className="font-semibold text-red-600 dark:text-red-400">
                            {formatCurrency(supplier.balance || 0)}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(supplier)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(supplier)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(supplier.id)}
                              className={
                                deleteConfirmId === supplier.id
                                  ? 'text-white bg-red-600 hover:bg-red-700'
                                  : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                              }
                            >
                              {deleteConfirmId === supplier.id ? (
                                <>تأكيد؟</>
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Supplier Form Modal */}
        <SupplierForm
          open={formOpen}
          onOpenChange={setFormOpen}
          supplier={selectedSupplier}
          onSuccess={() => {
            refetch();
            setSelectedSupplier(null);
          }}
        />

        {/* View Modal (placeholder) */}
        {viewOpen && selectedSupplier && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">تفاصيل المورد / Détails du fournisseur</h2>
                <Button variant="ghost" size="sm" onClick={() => setViewOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">الاسم / Nom</label>
                  <p className="font-medium">{selectedSupplier.name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">الهاتف / Téléphone</label>
                    <p className="font-medium">{selectedSupplier.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">البريد / Email</label>
                    <p className="font-medium">{selectedSupplier.email || '-'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">العنوان / Adresse</label>
                  <p className="font-medium">{selectedSupplier.address || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
